import { nextTick, watch, ref, readonly, type Ref } from "vue";
import { useRoute } from "vue-router";
import { useI18n, type Composer } from "vue-i18n";

const currentPageEntity = ref<any>(null);
const currentPageEntitySelectable = ref<any>(null);

export function usePageTitle(options?: {
  entityRef?: Ref<any>;
  selectable?: (entity: any) => any;
  i18n?: Composer;
}) {
  const route = useRoute();
  const { entityRef, selectable, i18n: i18nInstance } = options || {};

  const i18n = i18nInstance ? i18nInstance : useI18n();
  const baseTitle = i18n.t("generic.project.name");

  const updateTitle = async () => {
    await nextTick();

    let paths = [];

    if (route.meta?.title) {
      let title = route.meta.title as string;

      if (route.meta.titleParams && typeof route.meta.titleParams === "object") {
        const titleParams = route.meta.titleParams as Record<string, string>;

        if (titleParams.entity && i18n.te(titleParams.entity)) {
          title = i18n.t(title, { entity: i18n.t(titleParams.entity) });
        }
      }

      paths.unshift(i18n.te(title) ? i18n.t(title) : title);
    }

    if (currentPageEntity.value && currentPageEntitySelectable.value) {
      paths.unshift(currentPageEntitySelectable.value.title);
    }

    updateTitleFromPaths(paths);
  };

  const setPageEntity = (entityRef: Ref<any>, selectable: (entity: any) => any) => {
    watch(
      entityRef,
      (newValue) => {
        currentPageEntity.value = newValue;
        currentPageEntitySelectable.value = newValue ? selectable(newValue) : null;
        updateTitle();
      },
      { immediate: true }
    );
  };

  if (entityRef && selectable) {
    setPageEntity(entityRef, selectable);
  }

  const clearPageEntity = () => {
    currentPageEntity.value = null;
    currentPageEntitySelectable.value = null;
  };

  const updateTitleFromPaths = async (paths: string[]) => {
    document.title = [...paths, baseTitle].join(" | ");
  };

  return {
    updateTitle,
    updateTitleFromPaths,
    setPageEntity,
    clearPageEntity,
    entity: readonly(currentPageEntity),
    selectable: readonly(currentPageEntitySelectable),
  };
}
